import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  ArrowLeft,
  BriefcaseBusiness,
  CalendarPlus,
  CheckCircle2,
  CircleAlert,
  Clock3,
  ExternalLink,
  FileCheck2,
  Laptop,
  Link2,
  MessageSquareText,
  PencilLine,
  Play,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from 'lucide-react';
import {TagsInput} from '@mantine/core';
import {useMemo, useState} from 'react';
import {Link, useParams, useSearchParams} from '../router';
import {api, apiRequest} from '../api';
import {useWorkOperation} from '../hooks/useWorkOperation';
import {Badge, Modal, PrimaryButton, SecondaryButton, Surface, Textarea, TextInput} from '../design';
import {DocumentArtifact, Opportunity, PageResult, WorkState, humanizeStage} from '../types';
import {ErrorPanel, LoadingPanel, PageHeader, formatDateTime, formatRelativeDate} from '../components/PagePrimitives';
import {OpportunityNotes} from '../components/OpportunityNotes';
import {CoverLetterAction} from '../components/CoverLetterAction';

interface NoteRecord {
  id: string;
  title: string;
  markdown: string;
  updatedAt: string;
  version: number;
  revisionCount: number;
  linkedEntityType?: string;
}

interface ApplicationAttempt {
  id: string;
  site: 'linkedin' | 'indeed' | 'workday' | 'manual';
  status: WorkState | 'prepared' | 'submitted';
  pageCount: number;
  approvedPageCount: number;
  currentPage?: number;
  sourceUrl?: string;
  updatedAt: string;
  recoveryPackageUrl?: string;
}

interface AuditEntry {
  id: string;
  action: string;
  createdAt: string;
  actorLabel: string;
  summary: string;
}

interface CompanyRecord {
  id: string;
  name: string;
  summary?: string;
  websiteUrl?: string;
  sourceUrls?: string[];
  uncertain?: boolean;
  researchCompletedAt?: string;
  researchFacts?: Array<{text: string; sourceUrl: string}>;
}

interface ContactRecord {
  id: string;
  name: string;
  title?: string;
  emails?: string[];
  phone?: string;
  source?: 'user' | 'gmail' | 'application';
}

interface RelationshipRecord {
  id: string;
  name: string;
  stage: string;
  agency?: string;
}

interface ConversationRecord {
  id: string;
  summary: string;
  channel?: string;
  occurredAt?: string;
  updatedAt: string;
}

interface InterviewRecord {
  id: string;
  title: string;
  round: string;
  status: string;
  scheduledStartAt?: string;
  scheduledEndAt?: string;
  format?: string;
  location?: string;
}

interface TaskRecord {
  id: string;
  title: string;
  detail?: string;
  state: string;
  dueAt?: string;
}

interface CalendarRecord {
  id: string;
  summary?: string;
  state: string;
  providerEventId?: string;
  event?: {summary?: string; start?: {dateTime?: string; date?: string}};
}

interface PostingSnapshot {
  id?: string;
  title?: string;
  companyName?: string;
  canonicalUrl?: string;
  sourceUrl?: string;
  capturedAt?: string;
  immutable?: boolean;
}

interface OpportunityDetail extends Opportunity {
  description?: string;
  company?: CompanyRecord | null;
  contacts?: ContactRecord[];
  relationships?: RelationshipRecord[];
  conversations?: ConversationRecord[];
  applicationAttempts?: ApplicationAttempt[];
  tasks?: TaskRecord[];
  calendarProposals?: CalendarRecord[];
  calendarEvents?: CalendarRecord[];
  interviews?: InterviewRecord[];
  notes?: NoteRecord[];
  postingSnapshots?: PostingSnapshot[];
  auditHistory?: AuditEntry[];
  requiredQualifications?: string[];
  preferredQualifications?: string[];
  gaps?: string[];
  uncertainty?: string[];
  fitWarning?: string;
  companySummary?: {text?: string; sourceUrls?: string[]; uncertain?: boolean};
  applicationReadiness: {
    ready: boolean;
    checks: Array<{
      code: 'exact_tailored_resume' | 'approved_claims' | 'reviewed_answers' | 'recoverable_application';
      label: string;
      ready: boolean;
      reason: string;
      route: string;
      evidenceIds: string[];
    }>;
  };
  documents: DocumentArtifact[];
  contactNames: string[];
  interviewAt?: string;
  nextActionRecommendation?: {
    action: string;
    rationale: string;
    confidence: number;
    generatedAt: string;
    evidence: Array<{label: string; sourceType: string; sourceId?: string; sourceUrl?: string}>;
  };
}

type OpportunityEdit = {
  companyName: string;
  title: string;
  compensation: string;
  sourceUrl: string;
};
type CompanyEdit = {
  id: string; name: string; websiteUrl: string; summary: string; sourceUrls: string; uncertain: boolean;
};
type ContactEdit = {
  id: string; name: string; title: string; emails: string; phone: string;
};

type Tab = 'overview' | 'linked' | 'notes' | 'application' | 'history';

export const OpportunityDetailPage = () => {
  const {id = ''} = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState<Tab>(
    requestedTab === 'linked' || requestedTab === 'notes' || requestedTab === 'application' || requestedTab === 'history'
      ? requestedTab
      : 'overview',
  );
  const [editingLocations, setEditingLocations] = useState(false);
  const [prepareOperationId, setPrepareOperationId] = useState<string | null>(null);
  const [opportunityEdit, setOpportunityEdit] = useState<OpportunityEdit | null>(null);
  const [companyEdit, setCompanyEdit] = useState<CompanyEdit | null>(null);
  const [contactEdit, setContactEdit] = useState<ContactEdit | null>(null);
  const [draftLocations, setDraftLocations] = useState<string[]>([]);
  const [editingNextAction, setEditingNextAction] = useState(false);
  const [nextActionDraft, setNextActionDraft] = useState('');

  const opportunity = useQuery({
    queryKey: ['opportunity', id],
    queryFn: () => apiRequest<OpportunityDetail>('/opportunities/' + encodeURIComponent(id)),
    enabled: Boolean(id),
  });
  const bootstrap = useQuery({queryKey: ['bootstrap'], queryFn: api.bootstrap});
  const attempts = useQuery({
    queryKey: ['application-attempts', id],
    queryFn: () =>
      apiRequest<PageResult<ApplicationAttempt>>(
        '/application-attempts?opportunityId=' + encodeURIComponent(id),
      ),
    enabled: Boolean(id),
  });
  const history = useQuery({
    queryKey: ['audit-events', 'opportunity', id],
    queryFn: () =>
      apiRequest<PageResult<AuditEntry>>('/audit-events?opportunityId=' + encodeURIComponent(id)),
    enabled: Boolean(id),
  });

  const prepare = useMutation({
    mutationFn: () =>
      apiRequest<{operationId: string; state: WorkState}>(
        '/opportunities/' + encodeURIComponent(id) + '/prepare',
        {method: 'POST'},
      ),
    onSuccess: (result) => setPrepareOperationId(result.operationId),
  });
  const prepareInvalidations = useMemo(() => [
    ['action-items'],
    ['application-attempts', id],
    ['opportunity', id],
    ['documents'],
  ] as const, [id]);
  const prepareOperation = useWorkOperation(prepareOperationId, prepareInvalidations);
  const preparationActive = prepareOperation.data?.state === 'queued' || prepareOperation.data?.state === 'running';

  const proposal = useMutation({
    mutationFn: () =>
      apiRequest<{id: string; state: 'waiting_for_user'}>(
        '/calendar/proposals',
        {method: 'POST', body: JSON.stringify({opportunityId: id})},
      ),
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['action-items']}),
  });

  const saveLocations = useMutation({
    mutationFn: (locations: string[]) => apiRequest<OpportunityDetail>(
      '/opportunities/' + encodeURIComponent(id),
      {method: 'PATCH', body: JSON.stringify({locations, location: locations.join(' · ')})},
    ),
    onSuccess: (updated) => {
      queryClient.setQueryData(['opportunity', id], updated);
      setEditingLocations(false);
    },
  });
  const saveOpportunity = useMutation({
    mutationFn: (draft: OpportunityEdit) => apiRequest<OpportunityDetail>(
      '/opportunities/' + encodeURIComponent(id),
      {
        method: 'PATCH',
        body: JSON.stringify({
          companyName: draft.companyName.trim(),
          title: draft.title.trim(),
          compensation: draft.compensation.trim(),
          ...(draft.sourceUrl.trim() ? {sourceUrl: draft.sourceUrl.trim()} : {}),
        }),
      },
    ),
    onSuccess: (updated) => {
      queryClient.setQueryData(['opportunity', id], updated);
      setOpportunityEdit(null);
    },
  });
  const saveCompany = useMutation({
    mutationFn: (draft: CompanyEdit) => apiRequest(
      '/companies/' + encodeURIComponent(draft.id),
      {
        method: 'PATCH',
        body: JSON.stringify({
          name: draft.name.trim(),
          websiteUrl: draft.websiteUrl.trim(),
          summary: draft.summary.trim(),
          sourceUrls: draft.sourceUrls.split(/\r?\n|,/).map((value) => value.trim()).filter(Boolean),
          uncertain: draft.uncertain,
        }),
      },
    ),
    onSuccess: async () => {
      setCompanyEdit(null);
      await queryClient.invalidateQueries({queryKey: ['opportunity', id]});
    },
  });
  const saveContact = useMutation({
    mutationFn: (draft: ContactEdit) => apiRequest(
      '/contacts/' + encodeURIComponent(draft.id),
      {
        method: 'PATCH',
        body: JSON.stringify({
          name: draft.name.trim(),
          title: draft.title.trim(),
          phone: draft.phone.trim(),
          emails: draft.emails.split(/\r?\n|,/).map((value) => value.trim()).filter(Boolean),
        }),
      },
    ),
    onSuccess: async () => {
      setContactEdit(null);
      await queryClient.invalidateQueries({queryKey: ['opportunity', id]});
    },
  });

  const saveNextAction = useMutation({
    mutationFn: (nextAction: string) => apiRequest<OpportunityDetail>(
      '/opportunities/' + encodeURIComponent(id),
      {method: 'PATCH', body: JSON.stringify({nextAction})},
    ),
    onSuccess: (updated) => {
      queryClient.setQueryData(['opportunity', id], updated);
      setEditingNextAction(false);
      void queryClient.invalidateQueries({queryKey: ['opportunities']});
    },
  });

  const latestAttempt = useMemo(() => attempts.data?.items[0], [attempts.data]);

  if (opportunity.isLoading) return <LoadingPanel label="Loading opportunity" />;
  if (opportunity.isError || !opportunity.data) return <ErrorPanel error={opportunity.error} />;

  const item = opportunity.data;
  const locations = item.locations?.length ? item.locations : item.location ? [item.location] : [];
  const sourcePostingUrl = item.postingSnapshots?.[0]?.canonicalUrl || item.postingSnapshots?.[0]?.sourceUrl || item.sourceUrl;
  const contactSourceLabel = (source?: ContactRecord['source']) => source === 'gmail'
    ? 'Email'
    : source === 'application'
      ? 'Application'
      : 'User entered';
  const intelligenceSourceLabel = (source: string) => ({
    user_entered: 'User entered',
    email: 'Email',
    job_posting: 'Job posting',
    public_web: 'Public web',
    calendar: 'Calendar',
    application: 'Application',
  }[source] || humanizeStage(source));
  const relatedEvents = [
    ...(item.interviews ?? []).map((interview) => ({
      id: 'interview-' + interview.id,
      title: interview.title,
      at: interview.scheduledStartAt,
      detail: [humanizeStage(interview.round), humanizeStage(interview.status), interview.format && humanizeStage(interview.format)].filter(Boolean).join(' · '),
      source: 'User entered',
    })),
    ...(item.calendarProposals ?? []).map((event) => ({
      id: 'calendar-' + event.id,
      title: event.event?.summary || event.summary || 'Private calendar event',
      at: event.event?.start?.dateTime || event.event?.start?.date,
      detail: humanizeStage(event.state),
      source: 'Calendar',
    })),
  ].sort((left, right) => String(left.at || '').localeCompare(String(right.at || '')));
  const SourceBadge = ({label, href}: {label: string; href?: string}) => href ? (
    <a className="pd-evidence-source" href={href} target="_blank" rel="noreferrer">
      {label}<ExternalLink size={11} aria-hidden="true" />
    </a>
  ) : <span className="pd-evidence-source">{label}</span>;
  const applicationAction = (preparedLabel: string, unpreparedLabel: string) => {
    const companion = bootstrap.data?.capabilities?.chromeCompanion;
    if (companion?.usable === false) {
      return (
        <PrimaryButton disabled title={companion.message} leftSection={<CircleAlert size={16} aria-hidden="true" />}>
          Chrome companion unavailable
        </PrimaryButton>
      );
    }
    if (attempts.isLoading || attempts.isFetching) {
      return (
        <PrimaryButton loading disabled>
          Checking application status
        </PrimaryButton>
      );
    }
    if (attempts.isError) {
      return (
        <PrimaryButton
          disabled
          leftSection={<CircleAlert size={16} aria-hidden="true" />}
          title="Retry the application status check before preparing another attempt"
        >
          Application status unavailable
        </PrimaryButton>
      );
    }
    if (!latestAttempt) {
      return (
        <PrimaryButton
          leftSection={<Sparkles size={16} aria-hidden="true" />}
          loading={prepare.isPending || preparationActive}
          onClick={() => prepare.mutate()}
        >
          {unpreparedLabel}
        </PrimaryButton>
      );
    }
    if (latestAttempt.sourceUrl) {
      return (
        <PrimaryButton
          component="a"
          href={latestAttempt.sourceUrl}
          target="_blank"
          rel="noreferrer"
          leftSection={<Play size={16} aria-hidden="true" />}
          rightSection={<ExternalLink size={14} aria-hidden="true" />}
        >
          {preparedLabel}
        </PrimaryButton>
      );
    }
    return (
      <PrimaryButton
        component={Link}
        to="/settings?focus=extension#browser-companion"
        leftSection={<Laptop size={16} aria-hidden="true" />}
      >
        Open Chrome companion
      </PrimaryButton>
    );
  };
  return (
    <>
      <Link to="/" className="pd-back-link">
        <ArrowLeft size={15} aria-hidden="true" />
        Opportunity board
      </Link>
      <PageHeader
        eyebrow={humanizeStage(item.stage)}
        title={item.title + ' · ' + item.companyName}
        description={item.fitSummary}
        actions={
          <div className="pd-detail-actions">
            {item.sourceUrl ? (
              <SecondaryButton
                component="a"
                href={item.sourceUrl}
                target="_blank"
                rel="noreferrer"
                rightSection={<ExternalLink size={14} />}
              >
                Source posting
              </SecondaryButton>
            ) : null}
            <SecondaryButton
              onClick={() => setOpportunityEdit({
                companyName: item.companyName,
                title: item.title,
                compensation: item.compensation ?? '',
                sourceUrl: item.sourceUrl ?? '',
              })}
            >
              Edit details
            </SecondaryButton>
            {applicationAction('Resume prepared application', 'Prepare application')}
          </div>
        }
      />

      {prepareOperation.data && prepareOperation.data.state !== 'succeeded' ? (
        <div className={prepareOperation.data.state.startsWith('failed') ? 'pd-stale-banner' : 'pd-inline-status'} role={prepareOperation.data.state.startsWith('failed') ? 'alert' : 'status'}>
          <span>{prepareOperation.data.state === 'running'
            ? 'Preparing the application with the latest approved evidence.'
            : prepareOperation.data.state === 'queued'
              ? 'Application preparation is queued.'
              : prepareOperation.data.message ?? 'Application preparation could not finish.'}</span>
        </div>
      ) : null}

      <div className="pd-detail-meta">
        <button
          type="button"
          className="pd-location-trigger"
          onClick={() => {
            setDraftLocations(locations);
            setEditingLocations((current) => !current);
          }}
        >
          {locations.length ? locations.join(' · ') : 'Add locations'}
        </button>
        <span>{item.compensation || 'Compensation unknown'}</span>
        <span>{item.fitScore == null ? 'Fit pending' : Math.round(item.fitScore) + ' fit'}</span>
        <span>Updated {formatRelativeDate(item.updatedAt)}</span>
      </div>
      {editingLocations ? (
        <Surface className="pd-location-editor">
          <TagsInput
            label="Locations"
            description="Add cities, states, or Remote. Press Enter after each one."
            placeholder="City, state, or Remote"
            value={draftLocations}
            onChange={setDraftLocations}
            maxTags={12}
            splitChars={[]}
          />
          <div className="pd-location-editor__actions">
            {!draftLocations.some((value) => value.toLocaleLowerCase() === 'remote') ? (
              <SecondaryButton onClick={() => setDraftLocations((values) => [...values, 'Remote'])}>Add Remote</SecondaryButton>
            ) : null}
            <SecondaryButton onClick={() => setEditingLocations(false)}>Cancel</SecondaryButton>
            <PrimaryButton loading={saveLocations.isPending} onClick={() => saveLocations.mutate(draftLocations)}>
              Save locations
            </PrimaryButton>
          </div>
        </Surface>
      ) : null}

      {item.fitWarning ? (
        <div className="pd-stale-banner" role="status">
          <div>
            <strong>Review the fit gap before proceeding.</strong>
            <span>{item.fitWarning}</span>
          </div>
        </div>
      ) : null}

      <CoverLetterAction opportunityId={id} />

      <div className="pd-profile-tabs" role="tablist" aria-label="Opportunity details">
        {([
          ['overview', 'Overview'],
          ['linked', 'Linked records'],
          ['notes', 'Notes'],
          ['application', 'Application'],
          ['history', 'Audit history'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            className={tab === value ? 'is-active' : ''}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <div className="pd-detail-grid">
          <Surface className="pd-detail-primary">
            <span className="pd-eyebrow">Fit brief</span>
            <h3>What lines up—and what does not.</h3>
            <div className="pd-qualification-columns">
              <section>
                <h4>
                  <CheckCircle2 size={17} aria-hidden="true" />
                  Required
                </h4>
                <ul>
                  {(item.requiredQualifications?.length ? item.requiredQualifications : ['No required qualifications were classified from the stored posting.']).map((value) => (
                    <li key={value}><span>{value}</span><SourceBadge label="Job posting" href={sourcePostingUrl} /></li>
                  ))}
                </ul>
              </section>
              <section>
                <h4>
                  <Sparkles size={17} aria-hidden="true" />
                  Preferred
                </h4>
                <ul>
                  {(item.preferredQualifications?.length ? item.preferredQualifications : ['No preferred qualifications were classified from the stored posting.']).map((value) => (
                    <li key={value}><span>{value}</span><SourceBadge label="Job posting" href={sourcePostingUrl} /></li>
                  ))}
                </ul>
              </section>
            </div>
            {item.gaps?.length ? (
              <div className="pd-gap-note">
                <CircleAlert size={18} aria-hidden="true" />
                <div>
                  <strong>Visible gaps</strong>
                  <ul>
                    {item.gaps.map((gap) => (
                      <li key={gap}><span>{gap}</span><span className="pd-source-pair"><SourceBadge label="Job posting" href={sourcePostingUrl} /><SourceBadge label="User entered" /></span></li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
          </Surface>
          <aside className="pd-detail-rail">
            <Surface>
              <BriefcaseBusiness size={19} aria-hidden="true" />
              <h4>Company evidence</h4>
              <p>{item.company?.summary || item.companySummary?.text || 'Research is queued for this active opportunity.'}</p>
              {item.company?.uncertain || item.companySummary?.uncertain ? <small>Source-limited; not independently verified.</small> : null}
              <div className="pd-evidence-sources">
                {(item.company?.sourceUrls || item.companySummary?.sourceUrls || []).slice(0, 3).map((url, index) => (
                  <SourceBadge key={url} label={`Public web ${index + 1}`} href={url} />
                ))}
              </div>
              {item.company?.researchCompletedAt ? <small>Researched {formatRelativeDate(item.company.researchCompletedAt)}</small> : null}
              {item.company ? (
                <SecondaryButton
                  leftSection={<PencilLine size={14} aria-hidden="true" />}
                  onClick={() => setCompanyEdit({
                    id: item.company!.id,
                    name: item.company!.name,
                    websiteUrl: item.company!.websiteUrl ?? '',
                    summary: item.company!.summary ?? '',
                    sourceUrls: (item.company!.sourceUrls ?? []).join('\n'),
                    uncertain: item.company!.uncertain ?? true,
                  })}
                >
                  Edit company
                </SecondaryButton>
              ) : null}
              {item.uncertainty?.length ? (
                <ul>
                  {item.uncertainty.map((unknown) => (
                    <li key={unknown}><span>{unknown}</span><SourceBadge label="Job posting" href={sourcePostingUrl} /></li>
                  ))}
                </ul>
              ) : null}
            </Surface>
            <Surface>
              <UsersRound size={19} aria-hidden="true" />
              <h4>People</h4>
              {item.contacts?.length ? (
                <ul className="pd-people-list">
                  {item.contacts.map((contact) => (
                    <li key={contact.id}>
                      <strong>{contact.name}</strong>
                      {contact.title ? <span>{contact.title}</span> : null}
                      {contact.emails?.map((email) => <a key={email} href={'mailto:' + email}>{email}</a>)}
                      <SourceBadge label={contactSourceLabel(contact.source)} />
                      <button
                        type="button"
                        onClick={() => setContactEdit({
                          id: contact.id,
                          name: contact.name,
                          title: contact.title ?? '',
                          emails: (contact.emails ?? []).join('\n'),
                          phone: contact.phone ?? '',
                        })}
                      >
                        Edit contact
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <p>No email contacts have been linked yet.</p>}
            </Surface>
            <Surface>
              <Clock3 size={19} aria-hidden="true" />
              <h4>Next step</h4>
              {editingNextAction ? (
                <form
                  className="pd-next-step-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    saveNextAction.mutate(nextActionDraft.trim());
                  }}
                >
                  <Textarea
                    label="Your next step"
                    description="Say what has to happen next so this opportunity does not go quiet."
                    value={nextActionDraft}
                    onChange={(event) => setNextActionDraft(event.currentTarget.value)}
                    maxLength={1_000}
                    autosize
                    minRows={2}
                  />
                  {saveNextAction.isError ? (
                    <p className="pd-form-error" role="alert">
                      {saveNextAction.error instanceof Error
                        ? saveNextAction.error.message
                        : 'The next step could not be saved.'}
                    </p>
                  ) : null}
                  <div className="pd-form-actions">
                    <SecondaryButton type="button" onClick={() => setEditingNextAction(false)}>
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton type="submit" loading={saveNextAction.isPending}>
                      Save next step
                    </PrimaryButton>
                  </div>
                </form>
              ) : (
                <>
                  <p><strong>{item.nextAction || item.nextActionRecommendation?.action || 'No next step chosen yet.'}</strong></p>
                  {item.nextActionRecommendation && (!item.nextAction || item.nextAction === item.nextActionRecommendation.action) ? (
                    <>
                      <p>{item.nextActionRecommendation.rationale}</p>
                      <div className="pd-evidence-sources">
                        <span className="pd-confidence">{Math.round(item.nextActionRecommendation.confidence * 100)}% confidence</span>
                        {item.nextActionRecommendation.evidence.map((evidence, index) => (
                          <SourceBadge key={evidence.sourceId || evidence.label + index} label={intelligenceSourceLabel(evidence.sourceType)} href={evidence.sourceUrl || undefined} />
                        ))}
                      </div>
                    </>
                  ) : null}
                  {!item.nextActionRecommendation && !item.nextAction ? (
                    <p>PursuitDeck will propose one after new correspondence, tasks, interviews, or application activity arrives.</p>
                  ) : null}
                  {item.nextActionRecommendation && item.nextAction && item.nextAction !== item.nextActionRecommendation.action ? (
                    <div className="pd-next-suggestion">
                      <p>Suggested: {item.nextActionRecommendation.action}</p>
                      <SecondaryButton
                        loading={saveNextAction.isPending}
                        onClick={() => saveNextAction.mutate(item.nextActionRecommendation!.action)}
                      >
                        Use suggestion
                      </SecondaryButton>
                    </div>
                  ) : null}
                  <SecondaryButton
                    onClick={() => {
                      setNextActionDraft(item.nextAction || item.nextActionRecommendation?.action || '');
                      setEditingNextAction(true);
                    }}
                  >
                    {item.nextAction ? 'Edit next step' : 'Set next step'}
                  </SecondaryButton>
                </>
              )}
            </Surface>
            <Surface>
              <CalendarPlus size={19} aria-hidden="true" />
              <h4>Related events</h4>
              {relatedEvents.length ? (
                <ul className="pd-event-list">
                  {relatedEvents.map((event) => (
                    <li key={event.id}>
                      <strong>{event.title}</strong>
                      <span>{event.at ? formatDateTime(event.at) : 'Time not scheduled'}</span>
                      <small>{event.detail}</small>
                      <SourceBadge label={event.source} />
                    </li>
                  ))}
                </ul>
              ) : <p>No related interviews or calendar events are tracked yet.</p>}
              <SecondaryButton
                disabled={bootstrap.data?.capabilities?.calendar.usable === false}
                title={bootstrap.data?.capabilities?.calendar.usable === false ? bootstrap.data.capabilities.calendar.message : undefined}
                loading={proposal.isPending}
                onClick={() => proposal.mutate()}
              >
                Add private event
              </SecondaryButton>
            </Surface>
          </aside>
        </div>
      ) : null}

      {tab === 'linked' ? (
        <section className="pd-linked-graph" aria-labelledby="opportunity-linked-records">
          <div className="pd-section-heading">
            <div>
              <span className="pd-eyebrow">Opportunity graph</span>
              <h2 id="opportunity-linked-records">Everything connected to this opportunity</h2>
            </div>
            <p>Company evidence, people, preparation, execution, and history stay attached to one durable record.</p>
          </div>
          <div className="pd-linked-graph-grid">
            <Surface>
              <div className="pd-card-topline">
                <h3>Company</h3>
                <Badge>{item.company ? 'linked' : 'missing'}</Badge>
              </div>
              {item.company ? (
                <div className="pd-linked-record-body">
                  <strong>{item.company.name}</strong>
                  <p>{item.company.summary || 'No source-backed company summary has been saved.'}</p>
                  {item.company.uncertain ? <small>Source-limited; review before relying on it.</small> : null}
                  {item.company.websiteUrl || item.company.sourceUrls?.[0] ? (
                    <a
                      href={item.company.websiteUrl || item.company.sourceUrls?.[0]}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open company source <ExternalLink size={13} aria-hidden="true" />
                    </a>
                  ) : null}
                </div>
              ) : <p>No durable company record is linked.</p>}
            </Surface>

            <Surface>
              <div className="pd-card-topline">
                <h3>Posting snapshots</h3>
                <Badge>{item.postingSnapshots?.length ?? 0}</Badge>
              </div>
              {item.postingSnapshots?.length ? (
                <ul className="pd-linked-record-list">
                  {item.postingSnapshots.map((snapshot, index) => (
                    <li key={snapshot.id || snapshot.canonicalUrl || String(index)}>
                      <strong>{snapshot.title || item.title}</strong>
                      <span>{snapshot.companyName || item.companyName}</span>
                      <small>
                        {snapshot.immutable ? 'Immutable snapshot' : 'Snapshot'}
                        {snapshot.capturedAt ? ' · ' + formatDateTime(snapshot.capturedAt) : ''}
                      </small>
                      {snapshot.canonicalUrl || snapshot.sourceUrl ? (
                        <a href={snapshot.canonicalUrl || snapshot.sourceUrl} target="_blank" rel="noreferrer">
                          Stored source <ExternalLink size={13} aria-hidden="true" />
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : <p>No immutable posting snapshot is linked.</p>}
            </Surface>

            <Surface>
              <div className="pd-card-topline">
                <h3>People & relationships</h3>
                <Badge>{(item.contacts?.length ?? 0) + (item.relationships?.length ?? 0)}</Badge>
              </div>
              {item.contacts?.length || item.relationships?.length ? (
                <ul className="pd-linked-record-list">
                  {item.contacts?.map((contact) => (
                    <li key={contact.id}>
                      <strong>{contact.name}</strong>
                      <span>{contact.title || 'Contact'} · {contactSourceLabel(contact.source)}</span>
                      {contact.emails?.map((email) => <a key={email} href={'mailto:' + email}>{email}</a>)}
                    </li>
                  ))}
                  {item.relationships?.map((relationship) => (
                    <li key={relationship.id}>
                      <Link to={'/relationships?focus=' + encodeURIComponent(relationship.id)}>
                        {relationship.name}
                      </Link>
                      <span>{relationship.agency || humanizeStage(relationship.stage)}</span>
                    </li>
                  ))}
                </ul>
              ) : <p>No contacts or recruiter relationships are linked.</p>}
            </Surface>

            <Surface>
              <div className="pd-card-topline">
                <h3>Conversations</h3>
                <Badge>{item.conversations?.length ?? 0}</Badge>
              </div>
              {item.conversations?.length ? (
                <ul className="pd-linked-record-list">
                  {item.conversations.map((conversation) => (
                    <li key={conversation.id}>
                      <strong>{conversation.summary}</strong>
                      <small>
                        {humanizeStage(conversation.channel || 'conversation')} · {formatDateTime(conversation.occurredAt || conversation.updatedAt)}
                      </small>
                    </li>
                  ))}
                </ul>
              ) : <p>No recruiter conversations are linked.</p>}
            </Surface>

            <Surface>
              <div className="pd-card-topline">
                <h3>Structured interviews</h3>
                <Badge>{item.interviews?.length ?? 0}</Badge>
              </div>
              {item.interviews?.length ? (
                <ul className="pd-linked-record-list">
                  {item.interviews.map((interview) => (
                    <li key={interview.id}>
                      <strong>{interview.title}</strong>
                      <span>{humanizeStage(interview.round)} · {humanizeStage(interview.status)}</span>
                      <small>
                        {interview.scheduledStartAt ? formatDateTime(interview.scheduledStartAt) : 'Time not scheduled'}
                        {interview.format ? ' · ' + humanizeStage(interview.format) : ''}
                        {interview.location ? ' · ' + interview.location : ''}
                      </small>
                    </li>
                  ))}
                </ul>
              ) : <p>No structured interview records are linked.</p>}
            </Surface>

            <Surface>
              <div className="pd-card-topline">
                <h3>Tasks & action items</h3>
                <Badge>{item.tasks?.length ?? 0}</Badge>
              </div>
              {item.tasks?.length ? (
                <ul className="pd-linked-record-list">
                  {item.tasks.map((task) => (
                    <li key={task.id}>
                      <strong>{task.title}</strong>
                      <span>{task.detail || humanizeStage(task.state)}</span>
                      <small>{humanizeStage(task.state)}{task.dueAt ? ' · due ' + formatDateTime(task.dueAt) : ''}</small>
                    </li>
                  ))}
                </ul>
              ) : <p>No tasks or action items are linked.</p>}
            </Surface>

            <Surface>
              <div className="pd-card-topline">
                <h3>Calendar proposals & events</h3>
                <Badge>
                  {item.calendarProposals?.length ?? 0} proposed · {item.calendarEvents?.length ?? 0} created
                </Badge>
              </div>
              {item.calendarProposals?.length ? (
                <ul className="pd-linked-record-list">
                  {item.calendarProposals.map((calendar) => (
                    <li key={calendar.id}>
                      <strong>{calendar.event?.summary || calendar.summary || 'Private calendar proposal'}</strong>
                      <span>{humanizeStage(calendar.state)}</span>
                      <small>
                        {calendar.providerEventId ? 'Private provider event created' : 'Awaiting candidate-approved mutation'}
                        {calendar.event?.start?.dateTime || calendar.event?.start?.date
                          ? ' · ' + formatDateTime(calendar.event.start.dateTime || calendar.event.start.date!)
                          : ''}
                      </small>
                    </li>
                  ))}
                </ul>
              ) : <p>No calendar proposals or provider events are linked.</p>}
            </Surface>

            <Surface>
              <div className="pd-card-topline">
                <h3>Documents & attempts</h3>
                <Badge>{item.documents.length + (item.applicationAttempts?.length ?? 0)}</Badge>
              </div>
              {item.documents.length || item.applicationAttempts?.length ? (
                <ul className="pd-linked-record-list">
                  {item.documents.map((document) => (
                    <li key={document.id}>
                      <strong>{document.name}</strong>
                      <span>{humanizeStage(document.kind)} · {humanizeStage(document.state)}</span>
                    </li>
                  ))}
                  {item.applicationAttempts?.map((attempt) => (
                    <li key={attempt.id}>
                      <strong>{humanizeStage(attempt.site)} application attempt</strong>
                      <span>{humanizeStage(attempt.status)} · {attempt.pageCount} pages</span>
                    </li>
                  ))}
                </ul>
              ) : <p>No documents or application attempts are linked.</p>}
            </Surface>

            <Surface>
              <div className="pd-card-topline">
                <h3>Notes & audit</h3>
                <Badge>{(item.notes?.length ?? 0) + (item.auditHistory?.length ?? 0)}</Badge>
              </div>
              {item.notes?.length || item.auditHistory?.length ? (
                <ul className="pd-linked-record-list">
                  {item.notes?.slice(0, 5).map((note) => (
                    <li key={note.id}>
                      <Link
                        to={'?tab=notes&note=' + encodeURIComponent(note.id)}
                        onClick={() => setTab('notes')}
                      >
                        {note.title || 'Untitled note'}
                      </Link>
                      <small>
                        {humanizeStage(note.linkedEntityType || 'opportunity')} note · Updated{' '}
                        {formatRelativeDate(note.updatedAt)}
                      </small>
                    </li>
                  ))}
                  {item.auditHistory?.slice(0, 5).map((entry) => (
                    <li key={entry.id}>
                      <strong>{entry.action.replaceAll('.', ' · ')}</strong>
                      <small>{formatDateTime(entry.createdAt)} · {entry.actorLabel}</small>
                    </li>
                  ))}
                </ul>
              ) : <p>No linked notes or audit events are available.</p>}
            </Surface>
          </div>
        </section>
      ) : null}

      {tab === 'notes' ? <OpportunityNotes opportunityId={id} /> : null}

      {tab === 'application' ? (
        <div className="pd-application-layout">
          <Surface className="pd-application-status">
            <div className="pd-card-topline">
              <span>Browser execution</span>
              <Badge>{latestAttempt?.status ?? 'not started'}</Badge>
            </div>
            <Laptop size={28} aria-hidden="true" />
            <h3>
              {latestAttempt ? humanizeStage(latestAttempt.site) + ' application in progress' : 'Prepare before opening the job site'}
            </h3>
            <p>
              The Chrome companion inspects the signed-in page locally. Every page receives a complete editable review
              before any fields are filled or buttons are pressed.
            </p>
            {attempts.isError ? (
              <div className="pd-attempt-check-error" role="alert">
                <span>
                  Existing application attempts could not be verified. Retry this check before preparing
                  anything new.
                </span>
                <SecondaryButton onClick={() => void attempts.refetch()}>
                  Retry application status
                </SecondaryButton>
              </div>
            ) : null}
            {latestAttempt ? (
              <div className="pd-resume-guidance">
                <Laptop size={18} aria-hidden="true" />
                <div>
                  <strong>Continue the existing prepared attempt</strong>
                  <p>
                    {latestAttempt.sourceUrl
                      ? 'Open the saved application, then invoke the Chrome companion and choose Resume. This does not prepare or create another attempt.'
                      : 'This attempt has no saved application URL. Return to the original application tab, open the Chrome companion, and choose Resume.'}
                  </p>
                  <Link to="/settings?focus=extension#browser-companion">
                    Open Chrome companion settings
                  </Link>
                </div>
              </div>
            ) : null}
            {latestAttempt ? (
              <div className="pd-progress-track" aria-label={latestAttempt.approvedPageCount + ' approved pages'}>
                <span style={{width: Math.min(100, (latestAttempt.approvedPageCount / Math.max(1, latestAttempt.pageCount)) * 100) + '%'}} />
              </div>
            ) : null}
            {applicationAction('Resume on desktop', 'Prepare application plan')}
          </Surface>
          <div className="pd-application-checklist" id="application-readiness">
            {item.applicationReadiness.checks.map((check) => {
              const ChecklistIcon = check.code === 'exact_tailored_resume'
                ? FileCheck2
                : check.code === 'approved_claims'
                  ? ShieldCheck
                  : check.code === 'reviewed_answers'
                    ? MessageSquareText
                    : Link2;
              return (
                <Surface key={check.code}>
                  <ChecklistIcon size={19} aria-hidden="true" />
                  <span>{check.label}</span>
                  <strong>{check.ready ? 'Ready' : 'Needs attention'}</strong>
                  <small>{check.reason}</small>
                  {!check.ready ? <Link to={check.route}>Fix this</Link> : null}
                </Surface>
              );
            })}
          </div>
        </div>
      ) : null}

      {tab === 'history' ? (
        history.isLoading ? (
          <LoadingPanel label="Loading audit history" />
        ) : history.isError ? (
          <ErrorPanel error={history.error} />
        ) : (
          <ol className="pd-audit-timeline">
            {history.data?.items.map((entry) => (
              <li key={entry.id}>
                <span aria-hidden="true" />
                <Surface>
                  <div className="pd-card-topline">
                    <strong>{entry.action.replaceAll('.', ' · ')}</strong>
                    <time dateTime={entry.createdAt}>{formatDateTime(entry.createdAt)}</time>
                  </div>
                  <p>{entry.summary}</p>
                  <small>{entry.actorLabel}</small>
                </Surface>
              </li>
            ))}
          </ol>
        )
      ) : null}

      <Modal opened={Boolean(opportunityEdit)} onClose={() => setOpportunityEdit(null)} title="Edit opportunity details" closeButtonProps={{'aria-label': 'Close opportunity editor'}} centered>
        {opportunityEdit ? (
          <form className="pd-modal-form" onSubmit={(event) => { event.preventDefault(); saveOpportunity.mutate(opportunityEdit); }}>
            <TextInput
              label="Company"
              required
              value={opportunityEdit.companyName}
              onChange={(event) => setOpportunityEdit({...opportunityEdit, companyName: event.currentTarget.value})}
            />
            <TextInput
              label="Role title"
              required
              value={opportunityEdit.title}
              onChange={(event) => setOpportunityEdit({...opportunityEdit, title: event.currentTarget.value})}
            />
            <TextInput
              label="Compensation"
              value={opportunityEdit.compensation}
              onChange={(event) => setOpportunityEdit({...opportunityEdit, compensation: event.currentTarget.value})}
            />
            <TextInput
              label="Source posting URL"
              type="url"
              value={opportunityEdit.sourceUrl}
              onChange={(event) => setOpportunityEdit({...opportunityEdit, sourceUrl: event.currentTarget.value})}
            />
            {saveOpportunity.isError ? <p className="pd-form-error" role="alert">{saveOpportunity.error instanceof Error ? saveOpportunity.error.message : 'The opportunity could not be saved.'}</p> : null}
            <div className="pd-form-actions">
              <SecondaryButton type="button" onClick={() => setOpportunityEdit(null)}>Cancel</SecondaryButton>
              <PrimaryButton type="submit" loading={saveOpportunity.isPending}>Save opportunity</PrimaryButton>
            </div>
          </form>
        ) : null}
      </Modal>

      <Modal opened={Boolean(companyEdit)} onClose={() => setCompanyEdit(null)} title="Edit company" closeButtonProps={{'aria-label': 'Close company editor'}} centered>
        {companyEdit ? (
          <form className="pd-modal-form" onSubmit={(event) => { event.preventDefault(); saveCompany.mutate(companyEdit); }}>
            <TextInput label="Company name" required value={companyEdit.name} onChange={(event) => setCompanyEdit({...companyEdit, name: event.currentTarget.value})} />
            <TextInput label="Company website" type="url" value={companyEdit.websiteUrl} onChange={(event) => setCompanyEdit({...companyEdit, websiteUrl: event.currentTarget.value})} />
            <Textarea label="Company summary" value={companyEdit.summary} onChange={(event) => setCompanyEdit({...companyEdit, summary: event.currentTarget.value})} autosize minRows={3} />
            <Textarea label="Company source URLs" description="One public source URL per line." value={companyEdit.sourceUrls} onChange={(event) => setCompanyEdit({...companyEdit, sourceUrls: event.currentTarget.value})} autosize minRows={2} />
            <label className="pd-check-control"><input type="checkbox" checked={companyEdit.uncertain} onChange={(event) => setCompanyEdit({...companyEdit, uncertain: event.currentTarget.checked})} />Source-limited or uncertain</label>
            {saveCompany.isError ? <p className="pd-form-error" role="alert">{saveCompany.error instanceof Error ? saveCompany.error.message : 'The company could not be saved.'}</p> : null}
            <div className="pd-form-actions"><SecondaryButton type="button" onClick={() => setCompanyEdit(null)}>Cancel</SecondaryButton><PrimaryButton type="submit" loading={saveCompany.isPending}>Save company</PrimaryButton></div>
          </form>
        ) : null}
      </Modal>

      <Modal opened={Boolean(contactEdit)} onClose={() => setContactEdit(null)} title="Edit contact" closeButtonProps={{'aria-label': 'Close contact editor'}} centered>
        {contactEdit ? (
          <form className="pd-modal-form" onSubmit={(event) => { event.preventDefault(); saveContact.mutate(contactEdit); }}>
            <TextInput label="Contact name" required value={contactEdit.name} onChange={(event) => setContactEdit({...contactEdit, name: event.currentTarget.value})} />
            <TextInput label="Contact title" value={contactEdit.title} onChange={(event) => setContactEdit({...contactEdit, title: event.currentTarget.value})} />
            <Textarea label="Contact emails" description="One email address per line." value={contactEdit.emails} onChange={(event) => setContactEdit({...contactEdit, emails: event.currentTarget.value})} autosize minRows={2} />
            <TextInput label="Contact phone" value={contactEdit.phone} onChange={(event) => setContactEdit({...contactEdit, phone: event.currentTarget.value})} />
            {saveContact.isError ? <p className="pd-form-error" role="alert">{saveContact.error instanceof Error ? saveContact.error.message : 'The contact could not be saved.'}</p> : null}
            <div className="pd-form-actions"><SecondaryButton type="button" onClick={() => setContactEdit(null)}>Cancel</SecondaryButton><PrimaryButton type="submit" loading={saveContact.isPending}>Save contact</PrimaryButton></div>
          </form>
        ) : null}
      </Modal>
    </>
  );
};
